import {
  createRng,
  IllegalMoveError,
  type GameEngine,
  type GameOptions,
  type RNG,
  type ScoreResult,
  type Seat,
} from "@bing/engine-core";
import {
  boardFull,
  emptyCells,
  landingRows,
  openColumns,
  openerOf,
  otherSeat,
  rebuildHeights,
  winningLineAt,
} from "./rules";
import {
  cellAt,
  COLS,
  DEFAULT_DOOZ_HANDS,
  DOOZ_HAND_COUNTS,
  ROWS,
  type DoozConfig,
  type DoozHandCount,
  type DoozHandResult,
  type DoozMove,
  type DoozOpeningRoll,
  type DoozState,
  type DoozView,
} from "./types";

/* ---- PACING -----------------------------------------------------------------------------
 * The CLIENT half of this contract lives in `apps/web/src/screens/DoozTable.tsx`, which
 * imports these very constants. Two copies of an animation's length is how a bot ends up
 * moving while a piece is still in the air.
 * ---------------------------------------------------------------------------------------- */

/** The fall, for a piece landing on the TOP row (it barely moves). */
export const DROP_FALL_BASE_MS = 220;
/** …plus this per row it has to fall through. A drop into an empty column is the longest. */
export const DROP_FALL_PER_ROW_MS = 30;
/** The breath after it lands, before anything else happens. */
export const DROP_SETTLE_MS = 120;
/**
 * How long a decided دست holds the board before the next one is dealt — long enough to read a
 * winning line you did not expect. It is also what a bot waits before playing `nextHand`, so in
 * solo (and against a bot online) the match advances itself; a human table advances on either
 * player's tap, or on the shared window's expiry.
 */
export const HAND_REVEAL_MS = 2600;

/**
 * How long the board holds a live OPENING CONTEST before a bot throws its own die — long enough
 * for the die already on the felt to be read, and for a solo player to see that the opponent
 * really did throw rather than the result simply appearing.
 *
 * ⚠️ IT MUST CLEAR THE DIE'S OWN TUMBLE. The client throws the shared 3-D cube
 * (`apps/web/src/game/backgammon-dice.tsx`, the same one منچ and تخته نرد use) for `DIE_THROW_MS`
 * = 1000 ms; a bot answering faster than that would land its die while the player's is still in
 * the air. Two copies of an animation's length is how that goes wrong, so this is deliberately
 * stated as a number the client's own constant is checked against in `dooz-table.test.tsx`.
 */
export const OPENING_THROW_MS = 1200;

/**
 * How long the RESOLVED contest is announced for — the DICE STAY ON THE FELT for all of it.
 *
 * ⚠️ IT IS SIZED FOR THE PLAYER WHO DID **NOT** THROW LAST. The contest resolves inside the same
 * `applyMove` that rolls the second die, so at a two-human table the loser of the race sees the
 * pair appear and the board take over almost at once — «when I roll, the result vanishes before
 * the opponent can see what happened». Long enough to read two numbers and a sentence, and then
 * gone: it announces who opens, it does not live on the board for the rest of the match (which is
 * what the first implementation did, and — through a stuck effect — could never stop doing).
 */
export const OPENING_ANNOUNCE_MS = 3600;

/**
 * How long the piece dropped into `row` is in the air. Bounded by construction:
 * {@link DROP_FALL_BASE_MS} for the top row up to `BASE + 5 × PER_ROW` = 370 ms for an empty
 * column — always under the half-second the brief asks for, whatever the board looks like.
 */
export function dropFallMs(row: number): number {
  return DROP_FALL_BASE_MS + (ROWS - 1 - row) * DROP_FALL_PER_ROW_MS;
}

function isHandCount(value: unknown): value is DoozHandCount {
  return typeof value === "number" && (DOOZ_HAND_COUNTS as readonly number[]).includes(value);
}

/**
 * The match length, from the room's validated rules override. Anything else — absent, 7 (which
 * حکم's shared schema accepts), a crafted string — falls back to the conventional three rather
 * than being refused: a stray value must never stop a table from dealing.
 */
function configFrom(opts: GameOptions): DoozConfig {
  const rules = (opts.rules ?? {}) as { handsToPlay?: unknown };
  return { handsToPlay: isHandCount(rules.handsToPlay) ? rules.handsToPlay : DEFAULT_DOOZ_HANDS };
}

/** A contest nobody has thrown in yet. */
const NEW_CONTEST: DoozOpeningRoll = { dice: [null, null], ties: 0 };

/** The live contest, defaulting a legacy row's missing field to "there isn't one". */
function contestOf(state: DoozState): DoozOpeningRoll | null {
  return state.openingRoll ?? null;
}

/**
 * THROW ONE SEAT'S OPENING DIE and resolve whatever it produced.
 *
 * ⚠️ THE PLAYERS THROW THESE. They used to be rolled inside `createInitialState`, both at once,
 * so the decision was made before anyone saw it and the client could only state the result —
 * «the dice are never rolled». Each seat now throws its own, in either order, and the higher one
 * opens hand 1. A TIE is simply thrown again, which is why {@link DoozState.opening} can never
 * hold a pair and the reader never has to explain a draw.
 */
function applyOpeningRoll(state: DoozState, seat: Seat): DoozState {
  const rng = createRng(state.rngState);
  const contest = contestOf(state) ?? NEW_CONTEST;
  const rolled = rng.range(1, 6);
  const dice: [number | null, number | null] = [contest.dice[0] ?? null, contest.dice[1] ?? null];
  dice[seat === 0 ? 0 : 1] = rolled;
  const [a, b] = dice;
  const moveCount = state.moveCount + 1;
  // Only one die on the felt so far — hold it and wait for the other seat.
  if (a === null || b === null) {
    return {
      ...state,
      openingRoll: { dice, ties: contest.ties },
      rngState: rng.getState(),
      moveCount,
    };
  }
  if (a === b) {
    // A tie: both dice go back on the table. The PAIR is held for display — this is the only
    // moment the second die exists at all, and without it the thrower watches their own throw
    // vanish under the word «مساوی» (تخته نرد's reported bug, avoided here by construction).
    return {
      ...state,
      openingRoll: { dice: [null, null], ties: contest.ties + 1, tied: [a, b] },
      rngState: rng.getState(),
      moveCount,
    };
  }
  const first: Seat = a > b ? 0 : 1;
  return {
    ...state,
    openingRoll: null,
    firstMover: first,
    turn: first,
    opening: [a, b],
    rngState: rng.getState(),
    moveCount,
  };
}

/** A fresh board for a new دست — the same shape a match starts with. */
function freshBoard(): { cells: number[]; heights: number[] } {
  const cells = emptyCells();
  return { cells, heights: rebuildHeights(cells) };
}

/** Deal the next دست: an empty board, the alternated opener, and no stale last move. */
function dealNextHand(state: DoozState): DoozState {
  const hand = state.hand + 1;
  const board = freshBoard();
  return {
    ...state,
    cells: board.cells,
    heights: board.heights,
    hand,
    turn: openerOf(state.firstMover, hand),
    pendingHand: null,
    // CLEARED, and that is not tidiness: the client draws a last-move marker from this, and a
    // marker left over from the previous hand points at a cell on a board that no longer exists.
    lastDrop: null,
    moveCount: state.moveCount + 1,
  };
}

/**
 * IS THE MATCH ALREADY DECIDED? — no remaining hand can change who wins it.
 *
 * ⚠️ «UNREACHABLE LEAD», NOT «A MAJORITY». With `r` hands still to play the trailer can gain at
 * most `r`, so the match is settled exactly when `lead > r`. 3–0 with two to play stops (3 > 2);
 * 2–1 with two to play does NOT, because the trailer can still level it — and a level match is a
 * different result, not a lesser one ({@link DoozState.tiedMatch}). A drawn hand pays neither
 * side, so it can only ever shorten `r`, never close a gap.
 */
function matchDecided(
  wins: readonly [number, number],
  handsPlayed: number,
  total: number,
): boolean {
  const remaining = total - handsPlayed;
  return Math.abs(wins[0] - wins[1]) > remaining;
}

/**
 * A دست has been decided. Bank it, and either park the match on {@link DoozState.pendingHand} —
 * the between-hands pause both seats may end — or close the match out.
 *
 * ⚠️ THE MATCH STOPS AS SOON AS IT IS DECIDED — 3–0 in a five-hand match ends there. It used to
 * run its full length so that «over 5 hands the die's winner opens 3 and their opponent 2» stayed
 * literally true; that bought a symmetry nobody can see at the price of dead rubbers everybody
 * can. Operator decision, 2026-09-03.
 */
function bankHand(state: DoozState, next: DoozState, result: DoozHandResult): DoozState {
  const wins: [number, number] = [state.wins[0], state.wins[1]];
  // Indexed explicitly rather than by the seat number: `noUncheckedIndexedAccess` is on, and
  // widening a tuple read to `number | undefined` here would hide a real out-of-range seat.
  if (result.winner === 0) wins[0] += 1;
  else if (result.winner === 1) wins[1] += 1;
  const draws = state.draws + (result.winner === null ? 1 : 0);
  const banked: DoozState = { ...next, wins, draws };
  if (
    state.hand < state.config.handsToPlay &&
    !matchDecided(wins, state.hand, state.config.handsToPlay)
  ) {
    return {
      ...banked,
      pendingHand: result,
      // The next hand's opener already holds the turn, so `dealNextHand` only has to clear the
      // board. Nothing reads `turn` while `pendingHand` is set — the pause is SIMULTANEOUS, legal
      // for either seat — so moving it early cannot change who may act.
      turn: openerOf(state.firstMover, state.hand + 1),
    };
  }
  const level = wins[0] === wins[1];
  return {
    ...banked,
    finalHand: result,
    winner: level ? null : wins[0] > wins[1] ? 0 : 1,
    tiedMatch: level,
  };
}

/** Drop a piece into `col` for `seat` and resolve whatever it produced. */
function applyDrop(state: DoozState, seat: Seat, col: number): DoozState {
  const row = state.heights[col] ?? ROWS;
  const cell = cellAt(row, col);
  const cells = state.cells.slice();
  cells[cell] = seat;
  const heights = state.heights.slice();
  heights[col] = row + 1;
  const next: DoozState = {
    ...state,
    cells,
    heights,
    turn: otherSeat(seat),
    lastDrop: { seat, col, row, cell },
    moveCount: state.moveCount + 1,
  };
  const line = winningLineAt(cells, cell);
  if (line) return bankHand(state, next, { winner: seat, line });
  // A full board with no four is a DRAW: no point to either side (the brief).
  if (boardFull(heights)) return bankHand(state, next, { winner: null, line: [] });
  return next;
}

/**
 * دوز (Connect Four) — pure engine.
 *
 * A hand is: drop into a column, the piece falls to the lowest empty cell, four in a row wins it
 * (horizontal, vertical or either diagonal); a full board with no four is a draw. A MATCH is
 * exactly `handsToPlay` hands — a die decides who opens the first one and the opener alternates
 * after, so over five hands the die's winner opens three and the other two.
 */
export const doozEngine: GameEngine<DoozState, DoozMove, DoozView> = {
  gameId: "dooz",

  createInitialState(opts: GameOptions, rng: RNG): DoozState {
    const config = configFrom(opts);
    const board = freshBoard();
    return {
      cells: board.cells,
      heights: board.heights,
      // Placeholders both — the CONTEST decides them, and nothing may act until it does.
      turn: 0,
      hand: 1,
      config,
      firstMover: 0,
      openingRoll: NEW_CONTEST,
      wins: [0, 0],
      draws: 0,
      pendingHand: null,
      finalHand: null,
      lastDrop: null,
      moveCount: 0,
      rngState: rng.getState(),
      winner: null,
      tiedMatch: false,
    };
  },

  legalMoves(state: DoozState, seat: Seat): DoozMove[] {
    if (state.finalHand !== null) return [];
    // THE CONTEST. Both seats may act, each throwing their OWN die, in either order — the game's
    // second simultaneous phase. A seat that has already thrown has nothing to do but wait.
    const contest = contestOf(state);
    if (contest !== null) {
      return contest.dice[seat === 0 ? 0 : 1] === null ? [{ kind: "openingRoll" }] : [];
    }
    // The between-hands pause: legal for EITHER seat, which is what makes it a simultaneous phase.
    if (state.pendingHand !== null) return seat === 0 || seat === 1 ? [{ kind: "nextHand" }] : [];
    if (state.turn !== seat) return [];
    return openColumns(state.heights).map((col) => ({ kind: "drop", col }) as const);
  },

  isLegal(state: DoozState, seat: Seat, move: DoozMove): boolean {
    if (state.finalHand !== null) return false;
    const contest = contestOf(state);
    if (contest !== null) {
      return move?.kind === "openingRoll" && contest.dice[seat === 0 ? 0 : 1] === null;
    }
    if (move?.kind === "openingRoll") return false;
    if (state.pendingHand !== null) return move?.kind === "nextHand" && (seat === 0 || seat === 1);
    if (move?.kind !== "drop") return false;
    if (state.turn !== seat) return false;
    const { col } = move;
    if (!Number.isInteger(col) || col < 0 || col >= COLS) return false;
    return (state.heights[col] ?? ROWS) < ROWS;
  },

  applyMove(state: DoozState, seat: Seat, move: DoozMove): DoozState {
    if (!doozEngine.isLegal(state, seat, move)) throw new IllegalMoveError(seat, move);
    if (move.kind === "openingRoll") return applyOpeningRoll(state, seat);
    if (move.kind === "nextHand") return dealNextHand(state);
    return applyDrop(state, seat, move.col);
  },

  isTerminal(state: DoozState): boolean {
    return state.finalHand !== null;
  },

  score(state: DoozState): ScoreResult {
    const [a, b] = state.wins;
    const level = a === b;
    return {
      seatScores: [a, b],
      winners: level ? [0, 1] : a > b ? [0] : [1],
      // ⚠️ ONLY THE ENGINE MAY SAY THIS. `winners.length === 2` is ALSO the shape of a partnership
      // win, so no caller downstream can tell a tie from one; a دوز match that finishes level has
      // to say so here or it is recorded as a win for both.
      ...(level ? { tied: true } : {}),
      detail: {
        hands: state.config.handsToPlay,
        wins: [a, b],
        draws: state.draws,
        firstMover: state.firstMover,
      },
    };
  },

  viewFor(state: DoozState, seat: Seat): DoozView {
    const result = state.pendingHand ?? state.finalHand;
    const contest = contestOf(state);
    // ⚠️ THE CONTEST IS PART OF «LIVE». Without it `legalCols` would be published during the
    // opening throw — computed off the PLACEHOLDER `turn` — and the board would accept a drop
    // before anybody had rolled.
    const live = contest === null && state.pendingHand === null && state.finalHand === null;
    return {
      you: seat,
      turn: state.turn,
      cells: state.cells,
      legalCols: live && state.turn === seat ? openColumns(state.heights) : [],
      landing: landingRows(state.heights),
      handsToPlay: state.config.handsToPlay,
      hand: state.hand,
      wins: [state.wins[0], state.wins[1]],
      draws: state.draws,
      opener: openerOf(state.firstMover, state.hand),
      ...(state.opening ? { opening: state.opening } : {}),
      ...(contest ? { openingRoll: contest } : {}),
      ...(state.lastDrop ? { lastDrop: state.lastDrop } : {}),
      moveCount: state.moveCount,
      ...(result ? { handResult: result } : {}),
      awaitingNextHand: state.pendingHand !== null,
      status: state.finalHand !== null ? "over" : "playing",
      ...(state.winner !== null ? { winner: state.winner } : {}),
      ...(state.tiedMatch ? { tied: true } : {}),
    };
  },

  /**
   * How long the room holds this position before letting a bot act.
   *
   * A decided hand gets {@link HAND_REVEAL_MS} — so a bot playing `nextHand` cannot wipe a
   * winning line off the board before either player has read it. A drop gets exactly the time
   * the piece is in the air plus its settle, so the opponent never moves into a falling piece.
   */
  postMoveDelayMs(state: DoozState): number | undefined {
    if (contestOf(state) !== null) return OPENING_THROW_MS;
    /*
     * ⚠️ THE CONTEST IS DECIDED AND NOTHING HAS BEEN DROPPED YET — HOLD FOR THE ANNOUNCEMENT.
     *
     * Without this the room lets a bot that WON the die drop its first piece the instant the
     * contest resolves, so the player never sees the numbers at all: «if the bot wins the roll, it
     * plays immediately — I never get to see the dice result». The client shows the pair for
     * `OPENING_ANNOUNCE_MS`; this is the same span on the server, so the two cannot drift.
     *
     * It can fire at most once per match: the first drop sets `lastDrop`, and `dealNextHand` only
     * ever clears it on hand 2+. A RESUME that lands here (a saved game whose contest is decided
     * but which has no piece on the board) gets the same beat, which is right — the player has
     * just arrived and the dice are the first thing on screen.
     */
    if (state.opening !== undefined && state.lastDrop === null && state.hand === 1)
      return OPENING_ANNOUNCE_MS;
    if (state.pendingHand !== null || state.finalHand !== null) return HAND_REVEAL_MS;
    if (state.lastDrop) return dropFallMs(state.lastDrop.row) + DROP_SETTLE_MS;
    return undefined;
  },
};
